/**
 * Apps Platform iframe postMessage protocol — child side.
 *
 * Contract: docs/apps-platform/contract-v1.1.md §2–3 (Rumio repo).
 *
 *   Parent → App:
 *     rumio:navigate    { path: string }    — change internal route
 *     rumio:session     { token: string }   — refresh response
 *     rumio:design      { tokens: object }  — live theme tokens
 *     rumio:launcher:toggle                  — open/close floating launcher
 *     rumio:reload                           — hard-reload after a new deploy (v1.13)
 *
 *   App → Parent:
 *     rumio:height      { height: number }  — auto-resize iframe
 *     rumio:location    { path: string }    — announce internal path
 *     rumio:session:refresh                  — request fresh JWT
 *     rumio:launcher:state { open: boolean } — announce launcher surface
 *     rumio:update-available { version }     — a newer build of this app is live (v1.13)
 */

type DesignTokens = Record<string, string | number>;

type ParentMessage =
  | { type: "rumio:navigate"; path: string }
  | { type: "rumio:session"; token: string }
  | { type: "rumio:design"; tokens: DesignTokens }
  | { type: "rumio:launcher:toggle" }
  | { type: "rumio:reload" };

export type NavigateHandler = (path: string) => void;

interface PostMessageOptions {
  onNavigate?: NavigateHandler;
  onSessionToken?: (token: string) => void;
}

/**
 * Hook up parent-message listeners. Returns a teardown function. Call once at
 * app mount; React's useEffect cleanup runs the teardown on unmount.
 */
export function installPostMessageListeners(opts: PostMessageOptions = {}): () => void {
  function onMessage(ev: MessageEvent) {
    const data = ev.data as ParentMessage | undefined;
    if (!data || typeof data !== "object" || typeof data.type !== "string") return;
    switch (data.type) {
      case "rumio:navigate":
        opts.onNavigate?.(data.path);
        break;
      case "rumio:session":
        opts.onSessionToken?.(data.token);
        break;
      case "rumio:design":
        applyDesignTokens(data.tokens);
        break;
      case "rumio:reload":
        // Host's update banner was clicked (contract v1.13). Hard-reload so we
        // pick up the new deploy — clearing our own caches/SW same-origin,
        // which the host can't do for us. The session is a cookie, so it
        // survives the reload.
        void reloadToLatest();
        break;
      // launcher:toggle is reserved for floating-launcher apps; not used here.
    }
  }
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

/** Map design tokens onto CSS custom properties on :root. */
function applyDesignTokens(tokens: DesignTokens) {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(tokens)) {
    // Tokens arrive without leading -- so we add it. Skip non-scalar values.
    if (typeof v !== "string" && typeof v !== "number") continue;
    root.style.setProperty(`--${k}`, String(v));
  }
}

/** Announce auto-resize height to the parent. Throttle with rAF. */
let pendingHeightFrame = 0;
export function announceHeight() {
  if (pendingHeightFrame) return;
  pendingHeightFrame = requestAnimationFrame(() => {
    pendingHeightFrame = 0;
    const h = document.documentElement.scrollHeight;
    window.parent?.postMessage({ type: "rumio:height", height: h }, "*");
  });
}

/** Announce internal path to the parent so it can sync the browser URL. */
export function announceLocation(path: string) {
  window.parent?.postMessage({ type: "rumio:location", path }, "*");
}

// ─── Deploy-freshness (apps platform contract v1.13) ───
//
// The build pipeline stamps the build id into a <meta name="build-version">
// tag (the loaded document's baseline) and a /version.txt served at the web
// root (the poll target). While embedded, we poll for a newer build and tell
// the host so it can offer a one-click reload via its own update banner.

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_POLL_DELAY_MS = 30 * 1000;

/** True when running inside an iframe (i.e. embedded in the Rumio host). */
export function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin throw means we're framed by a parent we can't read.
    return true;
  }
}

/** The build id baked into the document we loaded, or null in dev/unstamped. */
function getBaselineVersion(): string | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLMetaElement>('meta[name="build-version"]');
  const value = el?.content?.trim() || null;
  if (!value || value === "__BUILD_VERSION__") return null;
  return value;
}

/** The build id the server is currently advertising, or null on any error. */
async function fetchServerVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch("/version.txt", { cache: "no-store", signal });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    return text || null;
  } catch {
    return null;
  }
}

/** Tear down caches + service worker, then reload from origin. */
export async function reloadToLatest(): Promise<void> {
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      /* swallow */
    }
  }
  if (typeof window !== "undefined" && "caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      /* swallow */
    }
  }
  window.location.reload();
}

/**
 * Poll /version.txt and, when embedded and the server advertises a different
 * build than the loaded document, announce it once to the host via
 * `rumio:update-available`. The host then shows its "A new version of <app> is
 * available" banner and can request a reload (`rumio:reload`).
 *
 * No-ops outside an iframe or when there's no build baseline (dev / unstamped
 * builds). Also checks whenever the tab regains visibility. Returns a teardown.
 */
export function startVersionWatch(): () => void {
  if (typeof window === "undefined") return () => {};
  if (!inIframe()) return () => {};
  const baseline = getBaselineVersion();
  if (!baseline) return () => {};

  let fired = false;
  const controller = new AbortController();

  const check = async () => {
    if (fired) return;
    const server = await fetchServerVersion(controller.signal);
    if (!server) return;
    if (server !== baseline) {
      fired = true;
      window.parent?.postMessage(
        { type: "rumio:update-available", version: server },
        "*",
      );
    }
  };

  const initial = setTimeout(check, INITIAL_POLL_DELAY_MS);
  const interval = setInterval(check, POLL_INTERVAL_MS);
  const onVisibility = () => {
    if (document.visibilityState === "visible") void check();
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    controller.abort();
    clearTimeout(initial);
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
